import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Stage, Layer, Line } from 'react-konva';
import Flatpickr from 'react-flatpickr';
import { Russian } from 'flatpickr/dist/l10n/ru.js';
import 'flatpickr/dist/flatpickr.css';

function formatDateDMY(date) {
  if (!date) return '';
  if (Array.isArray(date)) date = date[0];
  if (!(date instanceof Date)) date = new Date(date);
  if (isNaN(date)) return '';
  const d = date.getDate().toString().padStart(2, '0');
  const m = (date.getMonth() + 1).toString().padStart(2, '0');
  const y = date.getFullYear();
  return `${d}.${m}.${y}`;
}

function useWindowWidth() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return width;
}

function TemplateDocIframe() {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitStatus, setSubmitStatus] = useState('');
  const [signedDocId, setSignedDocId] = useState(null);
  const [fileId, setFileId] = useState(null);
  const [contragentToken, setContragentToken] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [lines, setLines] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [currentStep, setCurrentStep] = useState(1); // 1: fields, 2: preview, 3: signature
  const [previewStatus, setPreviewStatus] = useState('');
  const [pdfPreview, setPdfPreview] = useState(null);
  const [flatpickrInstances, setFlatpickrInstances] = useState({});
  const [agreementChecked, setAgreementChecked] = useState(false);
  const { id } = useParams();
  const { authFetch } = useAuth();
  const stageRef = useRef();
  const windowWidth = useWindowWidth();
  const signatureWidth = windowWidth < 576 ? windowWidth - 32 : 800; // 16px padding on each side
  const signatureHeight = Math.round(signatureWidth * 3 / 4); // 4:3 aspect ratio
  const isMobile = windowWidth < 576;

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await authFetch(`/api/v1/template-docs/${id}`);
        if (response.ok) {
          const data = await response.json();
          setTemplate(data);
          // Initialize form data with empty values
          const initialFields = {};
          data.sign_doc_fields?.forEach(field => {
            initialFields[field.tag] = '';
          });
          setFormData(initialFields);
        } else {
          setError('Failed to fetch template');
        }
      } catch (error) {
        setError('Error fetching template: ' + error.message);
      } finally {
        setLoading(false);
      }
    };

    fetchTemplate();
  }, [id, authFetch]);

  // Initialize datepickers when template loads
  useEffect(() => {
    if (template) {
      // Clean up existing instances
      Object.values(flatpickrInstances).forEach(instance => {
        if (instance && instance.destroy) {
          instance.destroy();
        }
      });

      const newInstances = {};
      
      // Add a small delay to ensure DOM is ready
      setTimeout(() => {
        // Initialize datepickers for all date fields
        template.sign_doc_fields?.forEach(field => {
          if (field.data_type === 'date') {
            const inputElement = document.getElementById(field.tag);
            if (inputElement) {
              const instance = flatpickrInstances[field.tag] = {
                input: inputElement,
                setDate: (date, noCallBack) => {
                  inputElement.value = date;
                  if (!noCallBack) {
                    handleInputChange(field.tag, date);
                  }
                }
              };
            }
          }
        });
        
        setFlatpickrInstances(newInstances);
      }, 100);
    }
  }, [template]);

  // Cleanup Flatpickr instances on unmount
  useEffect(() => {
    return () => {
      Object.values(flatpickrInstances).forEach(instance => {
        if (instance && instance.destroy) {
          instance.destroy();
        }
      });
    };
  }, [flatpickrInstances]);

  // Sync Flatpickr input values with React state
  useEffect(() => {
    if (template) {
      template.sign_doc_fields?.forEach(field => {
        if (field.data_type === 'date' && flatpickrInstances[field.tag]) {
          const instance = flatpickrInstances[field.tag];
          const currentValue = formData[field.tag] || '';
          
          // Add null checks to prevent errors
          if (instance && instance.input && instance.setDate) {
            // Only update if the value is different to avoid infinite loops
            if (instance.input.value !== currentValue) {
              instance.setDate(currentValue, false); // false = don't trigger onChange
            }
          }
        }
      });
    }
  }, [formData, flatpickrInstances, template]);

  const handleInputChange = (tag, value) => {
    console.log(tag, value)
    setFormData(prev => ({
      ...prev,
      [tag]: value
    }));
  };

  const handlePreview = async (e) => {
    e.preventDefault();
    setPreviewStatus('previewing');

    try {
      // Transform the form data into the required format (excluding signature)
      const fieldsArray = template.sign_doc_fields.map(field => ({
        tag: field.tag,
        value: formData[field.tag] || ''
      }));

      const response = await authFetch('/api/v1/document-preview', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          template_id: id,
          fields: fieldsArray
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Assuming the response contains a base64 PDF
        setPdfPreview(data.preview_b64);
        setPreviewStatus('success');
        setCurrentStep(2); // Move to preview step
      } else {
        setPreviewStatus('error');
        console.error('Document preview failed');
      }
    } catch (error) {
      setPreviewStatus('error');
      console.error('Error generating document preview:', error);
    }
  };

  const handleMouseDown = (e) => {
    setIsDrawing(true);
    const pos = e.target.getStage().getPointerPosition();
    setLines([...lines, { points: [pos.x, pos.y] }]);
    setHasSignature(true);
  };

  const handleMouseMove = (e) => {
    if (!isDrawing) return;

    const stage = e.target.getStage();
    const point = stage.getPointerPosition();
    const lastLine = lines[lines.length - 1];
    lastLine.points = lastLine.points.concat([point.x, point.y]);

    setLines([...lines.slice(0, -1), lastLine]);
  };

  const handleMouseUp = () => {
    setIsDrawing(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('submitting');

    try {
      // Get the signature as base64
      let signBase64 = '';
      if (stageRef.current && lines.length > 0) {
        signBase64 = stageRef.current.toDataURL();
      }

      // Transform the form data into the required format
      const fieldsArray = template.sign_doc_fields.map(field => ({
        field_id: field.id,
        field_name: field.name,
        tag: field.tag,
        value: formData[field.tag] || ''
      }));

      // Add signature field
      fieldsArray.push({
        field_id: 'sign',
        field_name: 'Signature',
        tag: 'sign',
        value: signBase64
      });

      const response = await authFetch('/api/v1/signed-docs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          template_id: id,
          fields: fieldsArray
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSignedDocId(data.id);
        setFileId(data.id);
        setContragentToken(data.contragent_token);
        setFileName(data.file_name || null);
        setSubmitStatus('success');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
      console.error('Error submitting form:', error);
    }
  };

  const handleDownload = async () => {
    try {
      // Build the URL with conditional format parameter
      let downloadUrl = `/api/v1/signed-docs/${fileId}/download?contragent_token=${contragentToken}`;
       
      const response = await authFetch(downloadUrl);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        // Use filename from server if available, otherwise fallback to default
        const downloadFilename = fileName || `document-${fileId}.pdf`;
        a.download = downloadFilename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Failed to download file');
      }
    } catch (error) {
      setError('Error downloading file: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="container mt-4">
        <div className="alert alert-warning" role="alert">
          Template not found
        </div>
      </div>
    );
  }

  if (submitStatus === 'success') {
    return (
      <div className="container mt-4">
        <div className="card">
          <div className="card-body text-center">
            <h5 className="card-title mb-4">Спасибо за заполнение дополнительного соглашения!</h5>
            <p className="mb-4">Вы можете скачать подписанный документ по ссылке:</p>
            <button 
              className="btn btn-primary"
              onClick={handleDownload}
            >
              <i className="bi bi-download me-2"></i>
              Скачать документ
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      <div className="card">
        <div className="card-body">
          <h5 className="card-title mb-4">{template.name}</h5>
          
          {/* Step indicator */}
          <div className="mb-4">
            <div className={`d-flex align-items-center${isMobile ? ' flex-column gap-3' : ''}`}>
              <div className={`step-indicator ${currentStep >= 1 ? 'active' : ''}`} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px',
                borderRadius: '20px',
                backgroundColor: currentStep >= 1 ? '#007bff' : '#e9ecef',
                color: currentStep >= 1 ? 'white' : '#6c757d',
                fontWeight: '500',
                marginRight: '8px'
              }}>
                <span className="step-number" style={{ marginRight: '8px' }}>1</span>
                <span className="step-text">Заполнение полей</span>
              </div>
              <div className="step-connector" style={{
                width: '40px',
                height: '2px',
                backgroundColor: currentStep >= 2 ? '#007bff' : '#e9ecef',
                marginRight: '8px'
              }}></div>
              <div className={`step-indicator ${currentStep >= 2 ? 'active' : ''}`} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px',
                borderRadius: '20px',
                backgroundColor: currentStep >= 2 ? '#007bff' : '#e9ecef',
                color: currentStep >= 2 ? 'white' : '#6c757d',
                fontWeight: '500',
                marginRight: '8px'
              }}>
                <span className="step-number" style={{ marginRight: '8px' }}>2</span>
                <span className="step-text">Предпросмотр</span>
              </div>
              <div className="step-connector" style={{
                width: '40px',
                height: '2px',
                backgroundColor: currentStep >= 3 ? '#007bff' : '#e9ecef',
                marginRight: '8px'
              }}></div>
              <div className={`step-indicator ${currentStep >= 3 ? 'active' : ''}`} style={{
                display: 'flex',
                alignItems: 'center',
                padding: '8px 16px',
                borderRadius: '20px',
                backgroundColor: currentStep >= 3 ? '#007bff' : '#e9ecef',
                color: currentStep >= 3 ? 'white' : '#6c757d',
                fontWeight: '500'
              }}>
                <span className="step-number" style={{ marginRight: '8px' }}>3</span>
                <span className="step-text">Подпись документа</span>
              </div>
            </div>
          </div>

          {currentStep === 1 ? (
            // Step 1: Fields form
            <form onSubmit={handlePreview}>
              {template.sign_doc_fields?.map((field, index) => (
                <div key={index} className="mb-3">
                  <label htmlFor={field.tag} className="form-label">
                    <span dangerouslySetInnerHTML={{ __html: field.name }} />
                    {field.required && <span className="text-danger">*</span>}
                  </label>
                  {field.data_type === 'text' && (
                    <input
                      type="text"
                      className="form-control"
                      id={field.tag}
                      value={formData[field.tag] || ''}
                      onChange={(e) => handleInputChange(field.tag, e.target.value)}
                      required={field.required}
                    />
                  )}
                  {field.data_type === 'date' && (
                    <Flatpickr
                      className="form-control flatpickr-input"
                      id={field.tag}
                      value={formData[field.tag] || ''}
                      onChange={(dateArr) => handleInputChange(field.tag, formatDateDMY(dateArr[0]))}
                      placeholder="дд.мм.гггг"
                      required={field.required}
                      options={{
                        dateFormat: 'd.m.Y',
                        locale: Russian,
                        allowInput: true,
                        clickOpens: true,
                        closeOnSelect: true,
                        disableMobile: false
                      }}
                    />
                  )}
                  {field.data_type === 'integer' && (
                    <input
                      type="number"
                      className="form-control"
                      id={field.tag}
                      value={formData[field.tag] || ''}
                      onChange={(e) => handleInputChange(field.tag, e.target.value)}
                      required={field.required}
                    />
                  )}
                  {field.data_type === 'enum' && (
                    <select
                      className="form-select"
                      id={field.tag}
                      value={formData[field.tag] || ''}
                      onChange={(e) => handleInputChange(field.tag, e.target.value)}
                      required={field.required}
                    >
                      <option value="">Выберите вариант</option>
                      {field.params && field.params.enum_options && field.params.enum_options.map((option, optionIndex) => (
                        <option key={optionIndex} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              ))}
              
              <div className="mb-3">
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="agreement"
                    checked={agreementChecked}
                    onChange={(e) => setAgreementChecked(e.target.checked)}
                    required
                  />
                  <label className="form-check-label" htmlFor="agreement">
                    {template.alt_privacy_text && template.alt_privacy_text.trim() ? (
                      <span dangerouslySetInnerHTML={{ __html: template.alt_privacy_text }} />
                    ) : (
                      <>
                        Нажимая на кнопку, Вы даете <a href="/personal-agreement" target="_blank">Согласие на обработку своих персональных данных</a> в соответствии с <a href="/privacy" target="_blank">Политикой конфиденциальности</a>
                      </>
                    )}
                  </label>
                </div>
              </div>
              
              <div className="d-flex justify-content-between align-items-center">
                <div>
                  {previewStatus === 'error' && (
                    <div className="text-danger">Ошибка при создании предпросмотра</div>
                  )}
                </div>
                <button 
                  type="submit" 
                  className="btn btn-primary"
                  disabled={previewStatus === 'previewing'}
                >
                  {previewStatus === 'previewing' ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Создание предпросмотра...
                    </>
                  ) : (
                    'Предпросмотр документа'
                  )}
                </button>
              </div>
            </form>
          ) : currentStep === 2 ? (
            // Step 2: PDF Preview
            <div>
              <div className="alert alert-success mb-4">
                <i className="bi bi-check-circle me-2"></i>
                Предпросмотр документа создан успешно. Пожалуйста, проверьте документ перед подписанием.
              </div>
              
              <div className="mb-4">
                <h6>Предпросмотр документа:</h6>
                <div style={{ 
                  border: '1px solid #dee2e6', 
                  borderRadius: '0.375rem',
                  height: '600px',
                  overflow: 'hidden'
                }}>
                  {pdfPreview && (
                    <iframe
                      src={`data:application/pdf;base64,${pdfPreview}#toolbar=0&navpanes=0`}
                      width="100%"
                      height="100%"
                      style={{ border: 'none' }}
                      title="Document Preview"
                    />
                  )}
                </div>
              </div>
              
              <div className="d-flex justify-content-between align-items-center">
                <button 
                  type="button" 
                  className="btn btn-outline-secondary"
                  onClick={() => setCurrentStep(1)}
                >
                  Назад к полям
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => setCurrentStep(3)}
                >
                  Перейти к подписанию
                </button>
              </div>
            </div>
          ) : (
            // Step 3: Signature form
            <form onSubmit={handleSubmit}>
              <div className="alert alert-info mb-4">
                <i className="bi bi-info-circle me-2"></i>
                Документ проверен. Подпишите, пожалуйста, документ с помощью мышки при использовании компьютера или пальца при использовании смартфона
              </div>
              
              <div className="mb-3">
                <label className="form-label">
                Подпись
                  <span className="text-danger">*</span>
                </label>
                <div 
                  className="konva-signature-touch"
                  style={{ 
                    border: '1px solid #ced4da', 
                    borderRadius: '0.25rem',
                    width: '100%',
                    height: `${signatureHeight}px`,
                    backgroundColor: '#f8f9fa',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Stage
                    width={signatureWidth > 320 ? signatureWidth : 320}
                    height={signatureHeight}
                    onMouseDown={handleMouseDown}
                    onMousemove={handleMouseMove}
                    onMouseup={handleMouseUp}
                    onTouchStart={handleMouseDown}
                    onTouchMove={handleMouseMove}
                    onTouchEnd={handleMouseUp}
                    ref={stageRef}
                    style={{ cursor: 'crosshair', maxWidth: '100%', backgroundColor: '#fff' }}
                  >
                    <Layer>
                      {lines.map((line, i) => (
                        <Line
                          key={i}
                          points={line.points}
                          stroke="#284283"
                          strokeWidth={5}
                          tension={0.8}
                          lineCap="round"
                          lineJoin="round"
                        />
                      ))}
                    </Layer>
                  </Stage>
                </div>
              </div>
              
              <div className="d-flex justify-content-between align-items-center">
                <button 
                  type="button" 
                  className="btn btn-outline-secondary"
                  onClick={() => setCurrentStep(2)}
                >
                  Назад к предпросмотру
                </button>
                <div className="d-flex align-items-center">
                  {!hasSignature && (
                    <div className="text-muted me-3">
                      <i className="bi bi-info-circle me-1"></i>
                      Распишитесь для продолжения
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="text-danger me-3">Ошибка при отправке формы</div>
                  )}
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={submitStatus === 'submitting' || !hasSignature}
                  >
                    {submitStatus === 'submitting' ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Отправка...
                      </>
                    ) : (
                      'Отправить'
                    )}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default TemplateDocIframe; 